// German (du) copy for /on-demand-ftp, translated from copy.en.ts. Same shape (checked by copy.test.ts).
// "FTP auf Abruf" and "Web-Uploader" as in the site's footer (src/i18n/locales/de.json).
// The uploader's and FileZilla's own labels are in English, so «labels» are quoted as the app shows them.
import type { OnDemandFtpCopy } from "./copy.en";

const de: OnDemandFtpCopy = {
  seo: {
    title: "So funktioniert FTP auf Abruf | Photohawk",
    description:
      "Wenn FTP unverzichtbar ist, kann jede Person in deinem Arbeitsbereich im Web-Uploader ihren eigenen Photohawk-FTP-Server starten, in allen Plänen. In etwa 30 Sekunden bereit, unter einer persönlichen Adresse, die sich nie ändert, und nach einer Stunde ohne Aktivität schaltet er sich selbst ab.",
  },
  hero: {
    badge: "In allen Plänen",
    eyebrow: "Jetzt verfügbar",
    title: "So funktioniert FTP auf Abruf",
    subtitle:
      "Wenn FTP unverzichtbar ist, startest du im Web-Uploader deinen eigenen FTP-Server, wann immer du ihn brauchst. Er ist in etwa 30 Sekunden bereit und schaltet sich selbst ab, wenn du fertig bist.",
  },
  before: {
    title: "Bevor du loslegst: Der Web-Uploader ist schneller",
    body: "Der Web-Uploader ist der Standardweg zum Hochladen, in allen Plänen.",
    points: [
      { id: "parallel", text: "Er lädt viele Fotos gleichzeitig hoch, weit mehr als ein FTP-Programm wie FileZilla (standardmäßig 2 gleichzeitig und höchstens 10)." },
      { id: "adaptive", text: "Er passt sich deiner Verbindung an und nutzt sie voll aus." },
      { id: "live", text: "Vorschaubilder gehen zuerst hoch, sodass deine Galerie in Minuten online ist, während die Originale nachkommen." },
      { id: "resume", text: "Bricht deine Verbindung ab, macht er von selbst weiter." },
    ],
    button: "Web-Uploader öffnen",
    link: "Den neuen Web-Uploader ansehen",
  },
  what: {
    heading: "Dein eigener FTP-Server, wenn du einen brauchst",
    body: "Jede Person in deinem Arbeitsbereich kann im Web-Uploader ihren eigenen FTP-Server starten, in allen Plänen. Jede Person bekommt ihren eigenen Server unter einer persönlichen Adresse, die sich nie ändert, sodass du dein FTP-Programm nur einmal einrichtest. Wenn du ihn nicht nutzt, läuft nichts: Nach einer Stunde ohne Aktivität schaltet er sich selbst ab.",
    date: "1. November 2026",
    closing: "Unsere dauerhaft laufenden gemeinsamen FTP-Server werden am 1. November 2026 abgeschaltet. Du nutzt sie heute? Starte deinen eigenen Server und ändere den Host in deinem FTP-Programm.",
  },
  steps: {
    heading: "So funktioniert's",
    subtitle: "Beim ersten Mal sind es vier Schritte. Danach startest du deinen Server und lädst hoch.",
    items: [
      { id: "open", title: "Web-Uploader öffnen", body: "Geh auf `upload.photohawk.com` und melde dich an, oder öffne den Tab «Upload» einer Galerie." },
      { id: "start", title: "Server starten", body: "Klick oben auf «FTP server» und dann auf «Start FTP server». Nach etwa 30 Sekunden ist er bereit." },
      { id: "connect", title: "Adresse und Login in dein FTP-Programm kopieren", body: "Deine Adresse, Port 21, deine Photohawk-E-Mail-Adresse und dein Passwort. Die Adresse ändert sich nie." },
      { id: "upload", title: "Hochladen", body: "Lege deine Fotos in den Ordner der Galerie. Nach einer Stunde ohne Upload schaltet sich der Server ab." },
    ],
    button: "FTP-Server starten",
    buttonNote: "Öffnet den Web-Uploader mit deinem FTP-Server, bereit zum Starten.",
  },
  panel: {
    heading: "Was du siehst",
    body: "Alles steht im Bereich «Your FTP server» im Web-Uploader, mit einer Kopier-Schaltfläche neben jeder Angabe.",
    points: [
      { id: "address", text: "Deine Adresse, etwa `swift-heron-4821.ftp.photohawk.com`. Sie gehört dauerhaft dir, du richtest sie also nur einmal ein." },
      { id: "ip", text: "Auch die IP-Adresse des Servers wird angezeigt. Sie ändert sich bei jedem Start des Servers, nutze also möglichst die Adresse." },
      { id: "countdown", text: "Ein Countdown bis zum Abschalten, mit «Keep it running» und «Stop»." },
    ],
    alt: "Der Bereich „Your FTP server“ mit dem Status „Ready“ in Europa (Irland), dem Host swift-heron-4821.ftp.photohawk.com, der IP-Adresse, Port 21, dem Benutzernamen alex@example.com, passivem Modus und FTP über TLS sowie einem Countdown mit den Schaltflächen „Keep it running“ und „Stop“.",
  },
  details: {
    heading: "Deine Verbindungsdaten",
    subtitle: "Trag sie in dein FTP-Programm ein.",
    rows: [
      { id: "host", label: "Host", value: "Deine Adresse, z. B. `swift-heron-4821.ftp.photohawk.com` (oder die angezeigte IP-Adresse)" },
      { id: "port", label: "Port", value: "21" },
      { id: "user", label: "Benutzername", value: "Deine Photohawk-E-Mail-Adresse" },
      { id: "password", label: "Passwort", value: "Dein Photohawk-Passwort oder dein FTP-Passwort (siehe unten)" },
      { id: "mode", label: "Übertragungsmodus", value: "Passiv" },
      { id: "encryption", label: "Verschlüsselung", value: "Einfaches FTP oder FTPS (explizites FTP über TLS). Für FTPS den Hostnamen verwenden, nicht die IP-Adresse." },
    ],
    switching: "Du hast Photohawk-FTP schon genutzt? Benutzername, Passwort und Ordner bleiben gleich. Ändere nur den Host auf deine neue Adresse.",
  },
  password: {
    heading: "Ein eigenes FTP-Passwort, wenn du möchtest",
    body: "Statt dein Photohawk-Passwort in einem FTP-Programm zu speichern, klick im Bereich auf «Create FTP password». Es funktioniert nur auf deinem FTP-Server, mit deiner E-Mail-Adresse als Benutzername. Du kannst es kopieren, ein neues erstellen (das alte funktioniert dann innerhalb einer Minute nicht mehr) oder es jederzeit entfernen. Dein Photohawk-Passwort funktioniert weiterhin.",
  },
  folders: {
    heading: "Deine Ordner",
    body: "Du siehst dieselben Ordner wie bisher:",
    path: "/Galleries/<gallery>/<collection>",
    points: [
      { id: "single", text: "Eine Galerie mit nur einer Sammlung ist einfach `/Galleries/<gallery>`." },
      { id: "workspaces", text: "Du lädst in mehr als einen Arbeitsbereich hoch? Jeder hat seinen eigenen Ordner, mit seinen Galerien darin." },
      { id: "create", text: "Leg die Galerie und ihre Sammlungen zuerst in Photohawk an. Deine Fotos erscheinen kurz darauf in der Galerie, genau wie beim Web-Uploader." },
    ],
  },
  off: {
    heading: "Er schaltet sich selbst ab",
    body: "Dein Server schaltet sich nach 1 Stunde ohne Aktivität ab, du musst also an nichts denken. Der Bereich zeigt einen Countdown.",
    points: [
      { id: "keep", text: "«Keep it running» startet den Countdown neu, ohne dass du etwas hochlädst." },
      { id: "stop", text: "«Stop» schaltet ihn sofort ab, wenn du fertig bist." },
      { id: "caps", text: "Zur Sicherheit schaltet er sich außerdem nach 12 Stunden ab, wenn nichts mehr hochgeladen wird, und spätestens nach 24 Stunden." },
      { id: "again", text: "Starte ihn wieder, wann immer du willst, mit derselben Adresse und demselben Login." },
    ],
  },
  regions: {
    heading: "Regionen",
    body: "Betreibe deinen Server in Europa (Irland), US-Ost (N. Virginia) oder Asien-Pazifik (Malaysia). Die nächstgelegene Region wird für dich ausgewählt, und du kannst sie vor dem Start ändern.",
  },
  filezilla: {
    heading: "Beispiel: FileZilla einrichten",
    subtitle: "Andere FTP-Programme nutzen dieselben Einstellungen, manchmal unter etwas anderen Namen.",
    steps: [
      { id: "site", text: "Öffne «File → Site Manager» und klick auf «New site»." },
      { id: "protocol", text: "Protokoll: «FTP – File Transfer Protocol»." },
      { id: "host", text: "Host: deine Adresse, z. B. `swift-heron-4821.ftp.photohawk.com`. Port: `21`." },
      { id: "tls", text: "Verschlüsselung: «Use explicit FTP over TLS if available»." },
      { id: "login", text: "Verbindungsart: «Normal». Benutzer: deine Photohawk-E-Mail-Adresse. Passwort: dein FTP-Passwort oder dein Photohawk-Passwort." },
      { id: "passive", text: "Wähle im Tab «Transfer Settings» die Option «Passive». Klick auf «Connect» und öffne dann `Galleries`." },
    ],
    tip: "Du schickst einen großen Ordner? FileZilla überträgt standardmäßig 2 gleichzeitig und höchstens 10. Du kannst die Einstellung für gleichzeitige Übertragungen auf bis zu 10 erhöhen («Settings → Transfers») oder den Web-Uploader nutzen, der viel mehr gleichzeitig schickt.",
  },
  trouble: {
    heading: "Wenn etwas nicht klappt",
    items: [
      { id: "connect", title: "Keine Verbindung", body: "Prüf, ob im Bereich «Ready» steht. Wenn der Hostname gerade erst angelegt wurde, nimm für eine Minute die IP-Adresse." },
      { id: "listing", title: "Verbunden, aber die Ordnerliste hängt", body: "Schalte den passiven Modus ein." },
      { id: "logins", title: "„Too many failed logins“", body: "Warte 15 Minuten und prüf dann Benutzernamen (deine E-Mail-Adresse) und Passwort." },
    ],
  },
  faq: {
    heading: "Fragen",
    items: [
      {
        q: "Warum hat sich mein Server abgeschaltet?",
        a: "Er schaltet sich nach einer Stunde ohne Aktivität ab, damit er nie läuft, wenn du ihn nicht nutzt. Starte ihn jederzeit wieder: Adresse und Login bleiben gleich. Damit er weiterläuft, ohne dass du hochlädst, klick auf «Keep it running». Er schaltet sich außerdem nach 12 Stunden ab, wenn nichts mehr hochgeladen wird, und spätestens nach 24 Stunden.",
      },
      {
        q: "Können zwei Personen gleichzeitig Server betreiben?",
        a: "Ja. Alle in deinem Arbeitsbereich können ihren eigenen Server starten, je einen, unter ihrer eigenen Adresse. Nur du kannst dich bei deinem anmelden.",
      },
      {
        q: "Ist das sicher?",
        a: "Ja. Mit FTPS (FTP über TLS) auf deinem Hostnamen ist die Verbindung mit einem vertrauenswürdigen Zertifikat verschlüsselt. Nur dein Login funktioniert auf deinem Server, und nach wiederholten fehlgeschlagenen Anmeldungen sperrt der Server diese Verbindung für 15 Minuten.",
      },
      {
        q: "Kostet das extra?",
        a: "Nein. Dein eigener FTP-Server ist in allen Plänen enthalten.",
      },
      {
        q: "Gibt es ihn in allen Plänen?",
        a: "Ja, auch in Lite. Für den Alltag sind der Web-Uploader und Photohawk Studio schneller; dein eigener FTP-Server ist da, wenn FTP unverzichtbar ist.",
      },
      {
        q: "Was passiert mit dem alten gemeinsamen FTP?",
        a: "Die dauerhaft laufenden gemeinsamen FTP-Server werden am 1. November 2026 abgeschaltet und funktionieren bis dahin wie gewohnt. Wenn du FTP weiter nutzen willst, starte deinen eigenen Server und ändere den Host in deinem FTP-Programm auf deine neue Adresse. Benutzername, Passwort und Ordner bleiben gleich.",
      },
    ],
  },
  cta: {
    heading: "Bereit, wenn du es bist",
    body: "Für alltägliche Uploads nimm den Web-Uploader. Wenn FTP unverzichtbar ist, ist dein eigener Server nur einen Klick entfernt.",
    primary: "Web-Uploader öffnen",
    secondary: "FTP-Server starten",
  },
};

export default de;
